import type moment from 'moment'
import { dueDateAsDay, taskDueState } from './taskDueDate'
import type { BadgeColor } from 'app/components/ui/badge'

/**
 * How an open task reads on a chip, wherever a chip has to stand for one.
 *
 * The contacts table wrote this vocabulary first and it is the one staff have
 * learned, so it lives here rather than in that table: "Overdue · Aug 12" says
 * both that somebody missed it and by how far, and "Due Sep 2" answers the
 * only question a task chip is ever asked, which is when. A chip that says
 * "1 task open" makes the reader go and look.
 *
 * Everything here counts in whole calendar days. See `taskDueDate`: a due date
 * is a day, not an instant, and comparing it as an instant calls a task due
 * today overdue everywhere west of UTC.
 */

export type TaskBadgeStatus = `overdue` | `dueToday` | `upcoming` | `noDueDate`

export type TaskBadge = {
  text: string
  /** Every task in the bucket, one per line, for a title or tooltip. */
  tooltip: string
  status: TaskBadgeStatus
}

export type BadgeTask = {
  title?: string | null
  dueDate?: string | Date | null
  status?: string | null
}

/** The chip colour each state wears. Overdue is the only one that raises its voice. */
export const TASK_BADGE_COLOR: Record<TaskBadgeStatus, BadgeColor> = {
  overdue: `red`,
  dueToday: `blue`,
  upcoming: `gray`,
  noDueDate: `gray`,
}

export function taskBadgeTooltip(tasks: BadgeTask[]): string {
  return tasks
    .map((task) =>
      task.dueDate
        ? `${task.title} · Due ${dueDateAsDay(task.dueDate).format(`MMM D`)}`
        : `${task.title} · No due date`,
    )
    .join(`\n`)
}

function badgeFor(tasks: BadgeTask[], status: TaskBadgeStatus): TaskBadge {
  const single = tasks.length === 1
  const text =
    status === `overdue`
      ? single
        ? `Overdue · ${dueDateAsDay(tasks[0].dueDate!).format(`MMM D`)}`
        : `${tasks.length} Overdue`
      : status === `dueToday`
        ? single
          ? `Due Today`
          : `${tasks.length} Due Today`
        : status === `noDueDate`
          ? single
            ? `No due date`
            : `${tasks.length} Undated`
          : single
            ? `Due ${dueDateAsDay(tasks[0].dueDate!).format(`MMM D`)}`
            : `${tasks.length} Upcoming`
  return { text, tooltip: taskBadgeTooltip(tasks), status }
}

/**
 * The open tasks split into the four states, most urgent first.
 *
 * Empty buckets are dropped, so the caller can take the first for the one that
 * matters most or render them all.
 */
export function taskBuckets(tasks: BadgeTask[], now?: moment.MomentInput): [BadgeTask[], TaskBadgeStatus][] {
  const at = (state: ReturnType<typeof taskDueState>) => tasks.filter((task) => taskDueState(task, now) === state)
  const buckets: [BadgeTask[], TaskBadgeStatus][] = [
    [at(`overdue`), `overdue`],
    [at(`dueToday`), `dueToday`],
    [at(`upcoming`), `upcoming`],
    // Open tasks without a due date get their own bucket so they stay visible.
    [at(`none`), `noDueDate`],
  ]
  return buckets.filter(([items]) => items.length > 0)
}

/**
 * One badge per state, or a single badge when they all share one.
 *
 * Splitting a mixed set is what lets a row say "2 Overdue" and "1 Due Today"
 * rather than flattening both into a number that hides the missed one.
 */
export function taskBadges(tasks: BadgeTask[], now?: moment.MomentInput): TaskBadge[] {
  const nonEmpty = taskBuckets(tasks, now)
  return (nonEmpty.length > 1 ? nonEmpty : nonEmpty.slice(0, 1)).map(([items, status]) => badgeFor(items, status))
}

/**
 * The single badge to show where there is only room for one.
 *
 * The most urgent state wins, and its tooltip still lists every open task, so
 * nothing is hidden. A card is not a table: one chip that says the worst thing
 * true beats four chips nobody can read at that width.
 */
export function mostUrgentTaskBadge(tasks: BadgeTask[], now?: moment.MomentInput): TaskBadge | undefined {
  const nonEmpty = taskBuckets(tasks, now)
  if (!nonEmpty.length) return undefined
  const [items, status] = nonEmpty[0]
  return { ...badgeFor(items, status), tooltip: taskBadgeTooltip(tasks) }
}
